import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Anmälan av skolans eget innehåll — hela kedjan på ett ställe.
 *
 * Förälder anmäler (utan inloggning) → kvittens till anmälaren → notis till
 * hej@ och anton@ → kö i admin → beslut med obligatorisk motivering → besked
 * till skolan → allt loggat i `school_content_events` och återställbart.
 *
 * Ingenting raderas någonsin. Att dölja innehåll betyder att
 * `moderation_status` sätts till `doljd`; raden ligger kvar och kan sättas
 * tillbaka till `publicerad`.
 */

export const REPORT_REASONS = {
  felaktig_uppgift: "Felaktig uppgift",
  olampligt: "Olämpligt innehåll",
  personuppgift: "Personuppgift som inte borde synas",
  upphovsratt: "Upphovsrätt",
  annat: "Annat",
} as const;

export const CONTENT_TYPES = {
  media: "Bild eller film",
  profiltext: "Text på skolans profil",
  jobbannons: "Jobbannons",
} as const;

export type ReportReason = keyof typeof REPORT_REASONS;
export type ReportedContentType = keyof typeof CONTENT_TYPES;

const reportSchema = z.object({
  schoolUnitCode: z.string().trim().min(1).max(40),
  contentType: z.enum(["media", "profiltext", "jobbannons"]),
  contentId: z.string().trim().min(1).max(120),
  reason: z.enum(["felaktig_uppgift", "olampligt", "personuppgift", "upphovsratt", "annat"]),
  message: z.string().trim().max(2000).optional().default(""),
  reporterEmail: z.string().trim().email().max(200).optional().or(z.literal("")).default(""),
  formToken: z.string().trim().max(300).optional().default(""),
  companyWebsite: z.string().max(200).optional().default(""),
});

function caseNumber(id: string): string {
  return id.replace(/-/g, "").slice(0, 8).toUpperCase();
}

/** Publik anmälan. Kräver ingen inloggning — en förälder ska kunna säga till. */
export const reportContent = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => reportSchema.parse(raw ?? {}))
  .handler(async ({ data }) => {
    const { verifyFormGuard } = await import("@/lib/form-token.server");
    await verifyFormGuard({
      scope: "content-report",
      token: data.formToken,
      honeypot: data.companyWebsite,
      school: data.schoolUnitCode,
    });

    const { enforceRateLimit, rateLimitKey } = await import("@/lib/rate-limit.server");
    await enforceRateLimit(
      "content-report:ip",
      rateLimitKey(data.formToken),
      [
        { windowMinutes: 60, max: 5 },
        { windowMinutes: 1440, max: 20 },
      ],
      "Du har skickat flera anmälningar nyligen, så vi pausar tillfälligt.",
    );

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: unit } = await supabaseAdmin
      .from("education_units")
      .select("school_unit_code, name, slug")
      .eq("school_unit_code", data.schoolUnitCode)
      .maybeSingle();
    if (!unit) throw new Error("Vi hittar inte skolan som anmälan gäller.");

    const excerpt = await describeContent(supabaseAdmin, data.contentType, data.contentId);

    const { data: row, error } = await supabaseAdmin
      .from("content_reports")
      .insert({
        school_unit_code: unit.school_unit_code,
        school_slug: unit.slug,
        school_name: unit.name,
        content_type: data.contentType,
        content_id: data.contentId,
        content_excerpt: excerpt,
        reason: data.reason,
        message: data.message || null,
        reporter_email: data.reporterEmail || null,
      })
      .select("id, created_at")
      .single();
    if (error) throw new Error(error.message);

    const nummer = caseNumber(row!.id as string);
    const { sendReceiptEmail } = await import("@/lib/email-templates/send-email");
    const { REPORT_NOTICE_RECIPIENTS } = await import("@/lib/email-templates/registry");

    if (data.reporterEmail) {
      await sendReceiptEmail("content-report-receipt", data.reporterEmail, {
        idempotencyKey: `content-report-receipt-${row!.id}`,
        templateData: {
          schoolName: unit.name,
          reasonLabel: REPORT_REASONS[data.reason],
          message: data.message || null,
          caseNumber: nummer,
        },
      });
    }
    for (const mottagare of REPORT_NOTICE_RECIPIENTS) {
      await sendReceiptEmail("content-report-internal", mottagare, {
        idempotencyKey: `content-report-internal-${row!.id}-${mottagare}`,
        templateData: {
          schoolName: unit.name,
          schoolUnitCode: unit.school_unit_code,
          contentTypeLabel: CONTENT_TYPES[data.contentType],
          reasonLabel: REPORT_REASONS[data.reason],
          message: data.message || null,
          reporterEmail: data.reporterEmail || null,
          excerpt,
        },
      });
    }

    await logEvent(supabaseAdmin, {
      schoolUnitCode: unit.school_unit_code,
      action: "anmald",
      contentType: data.contentType,
      contentId: data.contentId,
      actorEmail: data.reporterEmail || null,
      details: { reason: data.reason, report_id: row!.id },
    });

    return { caseNumber: nummer, receivedAt: row!.created_at as string };
  });

/** Kort beskrivning av det anmälda innehållet, så admin ser vad det gäller. */
async function describeContent(
  admin: any,
  contentType: ReportedContentType,
  contentId: string,
): Promise<string | null> {
  if (contentType === "media") {
    const { data } = await admin
      .from("media_assets")
      .select("title, alt_text")
      .eq("id", contentId)
      .maybeSingle();
    return data ? [data.title, data.alt_text].filter(Boolean).join(" · ") || null : null;
  }
  if (contentType === "profiltext") {
    const { data } = await admin
      .from("school_profiles")
      .select("description")
      .eq("school_unit_code", contentId)
      .maybeSingle();
    return data?.description ? String(data.description).slice(0, 300) : null;
  }
  const { data } = await admin
    .from("job_postings")
    .select("title")
    .eq("id", contentId)
    .maybeSingle();
  return data?.title ?? null;
}

async function logEvent(
  admin: any,
  input: {
    schoolUnitCode: string;
    action: string;
    contentType: string;
    contentId: string;
    actorId?: string | null;
    actorEmail?: string | null;
    details?: Record<string, unknown>;
  },
): Promise<void> {
  await admin.from("school_content_events").insert({
    school_unit_code: input.schoolUnitCode,
    action: input.action,
    content_type: input.contentType,
    content_id: input.contentId,
    actor_id: input.actorId ?? null,
    actor_email: input.actorEmail ?? null,
    details: input.details ?? {},
  });
}

async function requireAdmin(supabase: any, userId: string): Promise<void> {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (data !== true) throw new Error("Endast administratörer kan hantera anmälningar.");
}

/** Kön i admin. */
export const listContentReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("content_reports")
      .select(
        "id, school_unit_code, school_slug, school_name, content_type, content_id, content_excerpt, reason, message, reporter_email, status, decision_note, decided_at, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const decisionSchema = z.object({
  reportId: z.string().uuid(),
  decision: z.enum(["dolj", "avvisa", "aterstall"]),
  note: z.string().trim().min(5, "Skriv en motivering på minst fem tecken.").max(1000),
});

/**
 * Beslut i ett ärende. Motiveringen är obligatorisk, beslutet loggas och
 * skolan får besked. `aterstall` sätter tillbaka innehållet publikt.
 */
export const decideContentReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => decisionSchema.parse(raw ?? {}))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: report } = await supabaseAdmin
      .from("content_reports")
      .select("id, school_unit_code, school_name, content_type, content_id, reporter_email")
      .eq("id", data.reportId)
      .maybeSingle();
    if (!report) throw new Error("Anmälan finns inte.");

    const status =
      data.decision === "dolj" ? "doljd" : data.decision === "avvisa" ? "avvisad" : "aterstalld";

    if (data.decision !== "avvisa") {
      await setContentVisibility(
        supabaseAdmin,
        report.content_type as ReportedContentType,
        report.content_id,
        data.decision === "dolj" ? "doljd" : "publicerad",
        data.note,
      );
    }

    const { error } = await supabaseAdmin
      .from("content_reports")
      .update({
        status,
        decision_note: data.note,
        decided_by: context.userId,
        decided_at: new Date().toISOString(),
      })
      .eq("id", data.reportId);
    if (error) throw new Error(error.message);

    await logEvent(supabaseAdmin, {
      schoolUnitCode: report.school_unit_code,
      action: data.decision === "dolj" ? "doljt" : data.decision === "avvisa" ? "avvisad_anmalan" : "aterstallt",
      contentType: report.content_type,
      contentId: report.content_id,
      actorId: context.userId,
      details: { note: data.note, report_id: report.id },
    });

    if (data.decision !== "avvisa") {
      await notifySchool(supabaseAdmin, report.school_unit_code, "content-decision-school", {
        schoolName: report.school_name ?? "skolan",
        contentTypeLabel: CONTENT_TYPES[report.content_type as ReportedContentType],
        decision: data.decision === "dolj" ? "doljd" : "aterstalld",
        note: data.note,
      });
    }

    return { status };
  });

/** Döljer eller visar det anmälda innehållet. Raderar aldrig. */
async function setContentVisibility(
  admin: any,
  contentType: ReportedContentType,
  contentId: string,
  moderationStatus: "doljd" | "publicerad",
  note: string,
): Promise<void> {
  if (contentType === "media") {
    await admin
      .from("media_assets")
      .update({ moderation_status: moderationStatus, moderation_note: note })
      .eq("id", contentId);
    return;
  }
  if (contentType === "profiltext") {
    await admin
      .from("school_profiles")
      .update({ moderation_status: moderationStatus, moderation_note: note })
      .eq("school_unit_code", contentId);
    return;
  }
  await admin
    .from("job_postings")
    .update({ status: moderationStatus === "doljd" ? "doljd" : "publicerad", review_note: note })
    .eq("id", contentId);
}

/** Mejlar skolans aktiva företrädare. */
async function notifySchool(
  admin: any,
  schoolUnitCode: string,
  templateName: string,
  templateData: Record<string, unknown>,
): Promise<void> {
  const { sendReceiptEmail } = await import("@/lib/email-templates/send-email");
  const { data: members } = await admin
    .from("school_members")
    .select("user_id")
    .eq("school_unit_code", schoolUnitCode)
    .eq("status", "active");
  for (const member of members ?? []) {
    const { data: authUser } = await admin.auth.admin.getUserById(member.user_id);
    const email = authUser?.user?.email;
    if (!email) continue;
    await sendReceiptEmail(templateName, email, { templateData });
  }
}

const suspendSchema = z.object({
  schoolUnitCode: z.string().trim().min(1).max(40),
  suspend: z.boolean(),
  note: z.string().trim().min(5, "Skriv en motivering på minst fem tecken.").max(1000),
});

/**
 * Stänger av eller slår på en skolas egna innehåll. Den officiella skolsidan
 * finns kvar — bara det skolan själv publicerat döljs, och allt går tillbaka.
 */
export const setSchoolSuspension = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => suspendSchema.parse(raw ?? {}))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();

    const { data: unit } = await supabaseAdmin
      .from("education_units")
      .select("name")
      .eq("school_unit_code", data.schoolUnitCode)
      .maybeSingle();

    if (data.suspend) {
      const { error } = await supabaseAdmin.from("school_suspensions").upsert(
        {
          school_unit_code: data.schoolUnitCode,
          reason: data.note,
          suspended_by: context.userId,
          suspended_at: now,
          lifted_at: null,
          lifted_by: null,
          lift_note: null,
        },
        { onConflict: "school_unit_code" },
      );
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin
        .from("school_suspensions")
        .update({ lifted_at: now, lifted_by: context.userId, lift_note: data.note })
        .eq("school_unit_code", data.schoolUnitCode);
      if (error) throw new Error(error.message);
    }

    await logEvent(supabaseAdmin, {
      schoolUnitCode: data.schoolUnitCode,
      action: data.suspend ? "avstangd" : "avstangning_havd",
      contentType: "skola",
      contentId: data.schoolUnitCode,
      actorId: context.userId,
      details: { note: data.note },
    });

    await notifySchool(supabaseAdmin, data.schoolUnitCode, "school-suspension", {
      schoolName: unit?.name ?? "skolan",
      suspended: data.suspend,
      note: data.note,
    });

    return { suspended: data.suspend };
  });

/** Avstängda skolor, för adminvyn. */
export const listSchoolSuspensions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("school_suspensions")
      .select("school_unit_code, reason, suspended_at, lifted_at, lift_note")
      .order("suspended_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data ?? [];
  });
